import {
  Tabs,
  message,
  Button,
  Input,
  Collapse,
  Typography,
  Tag,
  Select,
} from 'antd';
import { CopyOutlined, SearchOutlined, LoadingOutlined, FieldTimeOutlined } from '@ant-design/icons';
import _ from 'lodash';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import InputParameter from '../../../../../components/InputParameter';
import JsonBeautifyTab from '../JsonBeautifyTab';
import colors from '../../../../../utils/theme/colors';
// removed getUroSDK import as model types are no longer used

function ModelConfigCol({ handleGenerateImage, modelsState, model, fetchModels, onModelSelect }) {
  // Replace hookstate with useState
  const [submitForm, setSubmitForm] = useState({
    model_id: null,
    params: {},
    available_params: [],
    lastModelParams: {},
  });
  const [activeTab, setActiveTab] = useState('basic');
  const [jsonEdit, setJsonEdit] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(!model?.model_id); // open when no model selected
  const [searchValue, setSearchValue] = useState(modelsState?.params?.model_name || '');
  const debouncedFetchRef = useRef(_.debounce((name, baseParams, fetch) => {
    if (fetch) {
      fetch({ ...baseParams, model_name: name });
    }
  }, 300));
  const searchInputRef = useRef(null);

  // Sync JSON when form changes
  useEffect(() => {
    if (activeTab === 'json') {
      setJsonEdit(
        JSON.stringify(
          {
            model_id: submitForm.model_id,
            params: submitForm.params,
          },
          null,
          2
        )
      );
    }
  }, [submitForm.model_id, submitForm.params, activeTab]);

  // Sync form when JSON changes
  const handleJsonChange = (e) => {
    setJsonEdit(e.target.value);
    try {
      const obj = JSON.parse(e.target.value);
      setSubmitForm((prev) => ({
        ...prev,
        model_id: obj.model_id !== undefined ? obj.model_id : prev.model_id,
        params: obj.params !== undefined ? obj.params : prev.params,
      }));
    } catch (err) {
      // Ignore parse errors while typing
    }
  };

  // Reset to defaults
  const handleReset = () => {
    if (submitForm.lastModelParams && submitForm.available_params.length) {
      const params = {};
      submitForm.available_params.forEach((param) => {
        params[param.parameter_name] = param.default_value;
      });
      setSubmitForm((prev) => ({
        ...prev,
        params,
      }));
      message.success('Reset to model defaults');
    }
  };

  // Required params guard helpers
  function isEmptyValue(param, value) {
    if (value === null || value === undefined) return true;
    switch (param.data_type) {
      case 'string':
        return String(value).trim() === '';
      case 'number':
      case 'float':
        return value === '' || Number.isNaN(Number(value));
      case 'boolean':
        return value === undefined || value === null;
      case 'object[]':
        return !Array.isArray(value) || value.length === 0;
      case 'object':
        if (typeof value === 'string') {
          try {
            JSON.parse(value);
            return false;
          } catch {
            return String(value).trim() === '';
          }
        }
        return typeof value !== 'object' || value === null;
      default:
        return false;
    }
  }

  function getMissingRequiredParams() {
    const paramsList = submitForm.available_params || [];
    return paramsList
      .filter((p) => Number(p.is_required) === 1 || p.is_required === true)
      .filter((p) => isEmptyValue(p, submitForm.params[p.parameter_name]));
  }

  const onHandleGenerateImage = () => {
    // Guard: prevent generate if any required params are missing
    const missing = getMissingRequiredParams();
    if (missing.length) {
      const names = missing.map((p) => p.parameter_title || p.parameter_name).join(', ');
      message.warning(`Please fill required parameter(s): ${names}`);
      return;
    }
    const params = submitForm.params;
    const formattedParams = {};
    let hasError = false;
    Object.keys(params).forEach((key) => {
      const param = submitForm.available_params.find(
        (p) => p.parameter_name === key
      );
      if (param) {
        const value = params[key];
        const isEmpty = value === '' || value === null || value === undefined;
        if (!(Number(param.is_required) === 1 || param.is_required === true) && isEmpty) {
          return;
        }
        switch (param.data_type) {
          case 'object[]':
            if (Array.isArray(value)) {
              formattedParams[key] = value.map((el) => {
                if (typeof el === 'string') {
                  try {
                    const parsed = JSON.parse(el);
                    if (typeof parsed === 'object' && parsed !== null) {
                      return parsed;
                    }
                    return el;
                  } catch {
                    return el;
                  }
                }
                return el;
              });
            } else if (typeof value === 'string') {
              try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                  formattedParams[key] = parsed.map((el) => {
                    if (typeof el === 'string') {
                      try {
                        const parsedEl = JSON.parse(el);
                        if (typeof parsedEl === 'object' && parsedEl !== null) {
                          return parsedEl;
                        }
                        return el;
                      } catch {
                        return el;
                      }
                    }
                    return el;
                  });
                } else {
                  formattedParams[key] = parsed;
                }
              } catch {
                formattedParams[key] = value;
              }
            }
            break;
          case 'number':
            if (typeof value === 'number') {
              formattedParams[key] = value;
            } else if (!isNaN(Number(value))) {
              formattedParams[key] = Number(value);
            } else {
              hasError = true;
              message.error(`${param.parameter_name} must be a number.`);
            }
            break;
          case 'float':
            if (typeof value === 'number') {
              formattedParams[key] = value;
            } else if (!isNaN(parseFloat(value))) {
              formattedParams[key] = parseFloat(value);
            } else {
              hasError = true;
              message.error(`${param.parameter_name} must be a float.`);
            }
            break;
          case 'boolean':
            if (typeof value === 'boolean') {
              formattedParams[key] = value;
            } else {
              formattedParams[key] = value === 'true' || value === true;
            }
            break;
          default:
            formattedParams[key] = value;
        }
      }
    });
    if (hasError) return;

    // Inject ratio+quality if dimension config is active (these aren't in available_params)
    if (dimensionConfig) {
      const ratio = submitForm.params?.ratio || dimensionConfig.default_ratio;
      const quality = submitForm.params?.quality || dimensionConfig.default_quality;
      if (ratio) formattedParams.ratio = ratio;
      if (quality && quality !== 'default') formattedParams.quality = quality;
    }

    handleGenerateImage({
      model_id: model?.model_id,
      params: formattedParams,
    });
  };

  // Group parameters by group_tag (or 'Other')
  const groupedParams = useMemo(() => {
    const params = submitForm.available_params || [];
    return _.groupBy(params, (p) => p.group_tag || 'Other');
  }, [submitForm.available_params]);

  // Parse structured dimension config from model
  const dimensionConfig = useMemo(() => {
    const dp = model?.resolution?.dimension_presets;
    if (!dp) return null;
    if (dp.ratios && dp.lookup) return dp;
    return null;
  }, [model?.resolution?.dimension_presets]);

  const availableRatios = dimensionConfig?.ratios || [];
  const availableQualities = dimensionConfig?.qualities || [];
  const hasQualitySelector = availableQualities.length > 1 || (availableQualities.length === 1 && availableQualities[0] !== 'default');

  // Current active ratio and quality from submitForm params
  const activeRatio = submitForm.params?.ratio || dimensionConfig?.default_ratio || availableRatios[0] || null;
  const activeQuality = submitForm.params?.quality || dimensionConfig?.default_quality || availableQualities[0] || null;

  const renderInputParameter = React.useCallback(
    (param) => {
      // Skip rendering width/height/aspect_ratio/resolution individually if dimension config exists
      const dimParams = ['width', 'height', 'aspect_ratio', 'resolution'];
      if (dimensionConfig && dimParams.includes(param.parameter_name)) {
        return null;
      }

      // Render normal parameter input
      return (
        <InputParameter
          key={param.parameter_name}
          param={param}
          value={submitForm.params[param.parameter_name]}
          onChange={(v) =>
            setSubmitForm((prev) => ({
              ...prev,
              params: { ...prev.params, [param.parameter_name]: v },
            }))
          }
        />
      );
    },
    [submitForm.params, dimensionConfig]
  );

  // When model changes, preserve parameter values that still exist in the
  // newly selected model. New parameters get their default values and
  // parameters that no longer exist are dropped.
  useEffect(() => {
    if (!model?.model_id) return;

    setSubmitForm((prev) => {
      const availableParams = (model.parameters || []).sort(
        (a, b) => (Number(b.is_required) === 1 || b.is_required === true ? 1 : 0) - (Number(a.is_required) === 1 || a.is_required === true ? 1 : 0)
      );

      // Build a map of the new model's default parameters
      const defaultParams = availableParams.reduce((acc, param) => {
        let defaultValue = param.default_value;
        
        // Convert string boolean values to actual booleans
        if (param.data_type === 'boolean') {
          if (typeof defaultValue === 'string') {
            defaultValue = defaultValue.toLowerCase() === 'true';
          } else if (typeof defaultValue !== 'boolean') {
            defaultValue = Boolean(defaultValue);
          }
        }
        
        acc[param.parameter_name] = defaultValue;
        return acc;
      }, {});

      // Preserve only values the user actually modified (i.e., differ from
      // the previous model's defaults) *and* still exist in the new model.
      const userModifiedKeys = Object.keys(prev.params || {}).filter((key) => {
        // Compare against previous model defaults (lastModelParams)
        const prevDefault = prev.lastModelParams?.[key];
        return prev.params[key] !== prevDefault;
      });

      const preservedParams = userModifiedKeys.reduce((acc, key) => {
        if (Object.prototype.hasOwnProperty.call(defaultParams, key)) {
          acc[key] = prev.params[key];
        }
        return acc;
      }, {});

      return {
        ...prev,
        model_id: model.model_id,
        available_params: availableParams,
        params: { ...defaultParams, ...preservedParams },
        lastModelParams: defaultParams,
      };
    });
  }, [model]);

  // Auto-open selector when there's no model selected
  useEffect(() => {
    if (!model?.model_id) {
      setIsPickerOpen(true);
    }
  }, [model?.model_id]);

  // When overlay opens, trigger a fresh fetch using current filters and sync search input
  useEffect(() => {
    if (!isPickerOpen) return;
    const initialName = modelsState?.params?.model_name || '';
    setSearchValue(initialName);
    if (fetchModels) {
      fetchModels({
        ...modelsState?.params,
        model_name: initialName,
      });
    }
    // Focus search input when overlay opens
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
    // intentionally minimal deps per project rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPickerOpen]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      if (debouncedFetchRef.current && debouncedFetchRef.current.cancel) {
        debouncedFetchRef.current.cancel();
      }
    };
  }, []);

  // Copy JSON to clipboard
  const handleCopyJson = () => {
    navigator.clipboard.writeText(jsonEdit);
    message.success('Copied JSON to clipboard');
  };

  const isGenerateDisabled = submitForm.isLoading || getMissingRequiredParams().length > 0;

  // Helper to format average cost nicely
  const formatCost = (v) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return 'N/A';
    const num = Number(v);
    if (num >= 1) return num.toFixed(2);
    if (num >= 0.01) return num.toFixed(4);
    return num.toPrecision(2);
  };

  // Top: model selector header
  const renderModelSummary = () => {
    if (!model?.model_id) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          />
          <div style={{ color: colors.foreground, fontSize: 14, fontWeight: 700 }}>Select a model</div>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img
          src={model.model_cover_image_url || 'https://png.pngtree.com/png-vector/20190820/ourmid/pngtree-no-image-vector-illustration-isolated-png-image_1694547.jpg'}
          alt={model.model_name}
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            objectFit: 'cover',
            background: colors.muted,
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 13,
            fontWeight: 700,
            color: colors.foreground,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} title={model.model_name}>{model.model_name}</div>
          {/* Meta row: type • cost • ETA */}
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 8, rowGap: 4, fontSize: 11, color: colors.muted_foreground }}>
            {model.model_type && (
              <span style={{ textTransform: 'capitalize' }}>{model.model_type}</span>
            )}
            {model.model_type && (
              <span style={{ opacity: 0.5 }}>•</span>
            )}
            <span>~ {formatCost(model.average_cost)} URO</span>
            {typeof model.model_eta === 'number' && (
              <>
                <span style={{ opacity: 0.5 }}>•</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FieldTimeOutlined /> {`${model.model_eta}s`}
                </span>
              </>
            )}
          </div>
          {model.model_description && (
            <div style={{
              marginTop: 6,
              fontSize: 11,
              color: colors.muted_foreground,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 260
            }} title={model.model_description}>
              {model.model_description}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderModelGridCard = (item) => {
    const isSelected = model?.model_id === item.model_id;
    return (
      <div
        key={item.model_id}
        onClick={() => {
          onModelSelect && onModelSelect(item);
          setIsPickerOpen(false);
        }}
        style={{
          cursor: 'pointer',
          background: isSelected
            ? 'linear-gradient(135deg, rgba(24, 119, 242, 0.15), rgba(114, 46, 209, 0.1))'
            : 'rgba(255,255,255,0.03)',
          borderRadius: 12,
          padding: 10,
          transition: 'all 0.2s ease',
          border: isSelected
            ? '2px solid rgba(24, 119, 242, 0.4)'
            : '1px solid rgba(255,255,255,0.08)'
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.25)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        {/* Media area 3:2 */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '3 / 2',
            borderRadius: 8,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <img
            src={item.model_cover_image_url || 'https://png.pngtree.com/png-vector/20190820/ourmid/pngtree-no-image-vector-illustration-isolated-png-image_1694547.jpg'}
            alt={item.model_name}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: isSelected ? 'none' : 'saturate(1)',
              transition: 'transform 0.25s ease, filter 0.25s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.03)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: 56,
              background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))'
            }}
          />
        </div>

        {/* Content */}
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 8 }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.foreground,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={item.model_name}
              >
                {item.model_name}
              </span>
            </div>
            {item.model_description && (
              <div
                style={{
                  fontSize: 11,
                  color: colors.muted_foreground,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={item.model_description}
              >
                {item.model_description}
              </div>
            )}
            {/* Meta row: type, cost and ETA */}
            <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {item.model_type && (
                <span style={{ fontSize: 11, color: colors.muted_foreground, textTransform: 'capitalize' }}>
                  {item.model_type}
                </span>
              )}
              <span style={{ fontSize: 11, color: colors.muted_foreground }}>
                ~ {formatCost(item.average_cost)} URO
              </span>
              {typeof item.model_eta === 'number' && (
                <span style={{ fontSize: 11, color: colors.muted_foreground, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FieldTimeOutlined /> {`${item.model_eta}s`}
                </span>
              )}
            </div>
          </div>
          {isSelected && (
            <Tag color='blue' style={{ marginInline: 0 }}>Selected</Tag>
          )}
        </div>
      </div>
    );
  };

  const renderModelCard = (item) => {
    const isSelected = model?.model_id === item.model_id;
    return (
      <div
        key={item.model_id}
        onClick={() => {
          onModelSelect && onModelSelect(item);
          setIsPickerOpen(false);
        }}
        style={{
          cursor: 'pointer',
          background: isSelected
            ? 'linear-gradient(135deg, rgba(24, 119, 242, 0.15), rgba(114, 46, 209, 0.1))'
            : 'rgba(255,255,255,0.03)',
          borderRadius: 10,
          padding: 10,
          marginBottom: 8,
          transition: 'all 0.2s ease',
          border: isSelected
            ? '2px solid rgba(24, 119, 242, 0.4)'
            : '1px solid rgba(255,255,255,0.08)'
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
            e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src={item.model_cover_image_url || 'https://png.pngtree.com/png-vector/20190820/ourmid/pngtree-no-image-vector-illustration-isolated-png-image_1694547.jpg'}
            alt={item.model_name}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              objectFit: 'cover',
              background: colors.muted,
              border: '1px solid rgba(255,255,255,0.1)'
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 8 }}>
              <span style={{
                fontSize: 12,
                fontWeight: 600,
                color: colors.foreground,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.model_name}
              </span>
            </div>
            {/* Meta row: type, cost and ETA */}
            <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {item.model_type && (
                <span style={{ fontSize: 11, color: colors.muted_foreground, textTransform: 'capitalize' }}>
                  {item.model_type}
                </span>
              )}
              <span style={{ fontSize: 11, color: colors.muted_foreground }}>
                ~ {formatCost(item.average_cost)} URO
              </span>
              {typeof item.model_eta === 'number' && (
                <span style={{ fontSize: 11, color: colors.muted_foreground, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FieldTimeOutlined /> {`${item.model_eta}s`}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        // overflow: 'auto',
        // overflowY: 'scroll',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        scrollbarColor: 'transparent',
        scrollbarGutter: 'stable',
        scrollbarWidth: '1px',
        scrollbarThumb: 'transparent',
      }}
    >
      <div className='no-scrollbar' style={{ flex: 1, overflow: 'auto', overflowY: 'scroll' }}>
        {/* Header with visual hierarchy matching 'Your Creations' */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ 
              fontSize: 24,
              background: 'linear-gradient(135deg, #1677ff, #722ed1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              🧠
            </div>
            <Typography.Text style={{ 
              color: colors.foreground, 
              fontSize: 20, 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #ffffff, #e6e6e6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Model
            </Typography.Text>
          </div>
          <Typography.Text style={{ 
            color: colors.muted_foreground, 
            fontSize: 14,
            display: 'block'
          }}>
            {model?.model_name ? model.model_name : 'Select a model to start'}
          </Typography.Text>
        </div>

        {/* Model selector: summary + expandable directory */}
        <div style={{ marginBottom: 12 }}>
          <div
            onClick={() => setIsPickerOpen((v) => !v)}
            style={{
              padding: !model?.model_id ? 2 : 0,
              borderRadius: 12,
              background: !model?.model_id
                ? 'linear-gradient(135deg, rgba(22, 119, 242, 0.8), rgba(114, 46, 209, 0.8))'
                : 'transparent',
              boxShadow: !model?.model_id ? '0 6px 24px rgba(114,46,209,0.25)' : 'none',
              cursor: 'pointer',
              transition: 'box-shadow 0.2s ease, background 0.2s ease'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)'
              }}
            >
              {renderModelSummary()}
              <div style={{ color: colors.muted_foreground, fontSize: 12, marginLeft: 8 }}>
                {isPickerOpen ? '▲' : '▼'}
              </div>
            </div>
          </div>
        </div>
        {/* Developer Mode Toggle */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          marginBottom: 12 
        }}>
          <button
            onClick={() => setActiveTab(activeTab === 'json' ? 'basic' : 'json')}
            style={{
              background: 'none',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6,
              padding: '4px 8px',
              color: colors.muted_foreground,
              fontSize: 11,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: 'inherit'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = 'rgba(255,255,255,0.2)';
              e.target.style.color = colors.foreground;
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'rgba(255,255,255,0.1)';
              e.target.style.color = colors.muted_foreground;
            }}
          >
            {activeTab === 'json' ? '← Back' : '{ } Developer'}
          </button>
        </div>

        {activeTab === 'json' ? (
          <div style={{ marginTop: 16 }}>
            <Typography.Text style={{ 
              color: colors.muted_foreground, 
              fontSize: 13,
              display: 'block',
              marginBottom: 16
            }}>
              Raw JSON parameters for debugging and advanced use
            </Typography.Text>
            <JsonBeautifyTab data={submitForm.params} />
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyJson}
              style={{ marginTop: 12 }}
            >
              Copy JSON
            </Button>
          </div>
        ) : (
          <Tabs
            activeKey={activeTab === 'json' ? 'basic' : activeTab}
            onChange={setActiveTab}
            defaultActiveKey='basic'
            type='line'
            items={[
              {
                key: 'basic',
                label: '⚡ Quick Settings',
                children: (
                  <div style={{ marginTop: 16 }}>
                    <Typography.Text style={{ 
                      color: colors.muted_foreground, 
                      fontSize: 13,
                      display: 'block',
                      marginBottom: 16
                    }}>
                      Essential settings for great results
                    </Typography.Text>
                    {/* Render ratio + quality selector if dimension config available */}
                    {dimensionConfig && availableRatios.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Ratio</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: hasQualitySelector ? 12 : 0 }}>
                          {availableRatios.map((ratio) => (
                            <button
                              key={ratio}
                              onClick={() => setSubmitForm((prev) => ({ ...prev, params: { ...prev.params, ratio } }))}
                              style={{
                                padding: '5px 10px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                background: activeRatio === ratio ? '#fa8b14' : 'rgba(255,255,255,0.08)',
                                color: activeRatio === ratio ? '#fff' : '#ccc',
                              }}
                            >{ratio}</button>
                          ))}
                          {dimensionConfig.has_auto && (
                            <button
                              onClick={() => setSubmitForm((prev) => ({ ...prev, params: { ...prev.params, ratio: 'auto' } }))}
                              style={{
                                padding: '5px 10px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                background: activeRatio === 'auto' ? '#fa8b14' : 'rgba(255,255,255,0.08)',
                                color: activeRatio === 'auto' ? '#fff' : '#ccc',
                              }}
                            >Auto</button>
                          )}
                        </div>
                        {hasQualitySelector && (
                          <>
                            <div style={{ marginBottom: 4, fontSize: 13, fontWeight: 500 }}>Quality</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {availableQualities.map((quality) => (
                                <button
                                  key={quality}
                                  onClick={() => setSubmitForm((prev) => ({ ...prev, params: { ...prev.params, quality } }))}
                                  style={{
                                    padding: '5px 10px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                    background: activeQuality === quality ? '#fa8b14' : 'rgba(255,255,255,0.08)',
                                    color: activeQuality === quality ? '#fff' : '#ccc',
                                  }}
                                >{quality}</button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Render all required parameters - include prompt since it's essential */}
                    {(() => {
                      // Get all required parameters, excluding width/height if dimension presets exist
                      const allRequiredParams = submitForm.available_params
                        ?.filter((p) => {
                          if (!(Number(p.is_required) === 1 || p.is_required === true)) return false;
                          // Skip dimension params if dimension config exists (already shown above)
                          if (dimensionConfig && ['width', 'height', 'aspect_ratio', 'resolution'].includes(p.parameter_name)) {
                            return false;
                          }
                          return true;
                        }) || [];
                      
                      return allRequiredParams.map((param) => (
                        <InputParameter
                          key={param.parameter_name}
                          param={param}
                          value={submitForm.params[param.parameter_name]}
                          onChange={(v) =>
                            setSubmitForm((prev) => ({
                              ...prev,
                              params: {
                                ...prev.params,
                                [param.parameter_name]: v,
                              },
                            }))
                          }
                        />
                      ));
                    })()}
                    
                    {/* Helpful micro-content when no required params */}
                    {submitForm.available_params.filter((p) => (Number(p.is_required) === 1 || p.is_required === true) && p.parameter_name !== 'prompt').length === 0 && (
                      <div style={{ marginTop: 24 }}>
                        {/* Example Prompts Carousel */}
                        <div style={{ marginBottom: 24 }}>
                          <div style={{ 
                            fontSize: 14, 
                            fontWeight: 600, 
                            color: colors.foreground,
                            marginBottom: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            ✨ Example Prompts
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {[
                              "A cyberpunk logo with neon blue accents",
                              "Portrait of a wise wizard, cinematic lighting",
                              "Minimalist mountain landscape at sunset",
                              "Futuristic city skyline, detailed architecture"
                            ].map((example, i) => (
                              <div
                                key={i}
                                onClick={() => {
                                  setSubmitForm(prev => ({
                                    ...prev,
                                    params: { ...prev.params, prompt: example }
                                  }));
                                }}
                                style={{
                                  padding: '8px 12px',
                                  background: 'rgba(255,255,255,0.03)',
                                  border: '1px solid rgba(255,255,255,0.08)',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  color: colors.muted_foreground,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(255,255,255,0.06)';
                                  e.target.style.borderColor = 'rgba(255,255,255,0.15)';
                                  e.target.style.color = colors.foreground;
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'rgba(255,255,255,0.03)';
                                  e.target.style.borderColor = 'rgba(255,255,255,0.08)';
                                  e.target.style.color = colors.muted_foreground;
                                }}
                              >
                                "{example}"
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Quick Tips */}
                        <div>
                          <div style={{ 
                            fontSize: 14, 
                            fontWeight: 600, 
                            color: colors.foreground,
                            marginBottom: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            💡 Pro Tips
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {[
                              "Use adjectives like 'cinematic', 'detailed', 'surreal' for better results",
                              "Specify lighting: 'soft lighting', 'dramatic shadows', 'golden hour'",
                              "Add style keywords: 'photorealistic', 'oil painting', 'digital art'",
                              "Include composition: 'close-up', 'wide shot', 'bird's eye view'"
                            ].map((tip, i) => (
                              <div
                                key={i}
                                style={{
                                  padding: '8px 12px',
                                  background: 'rgba(34, 197, 94, 0.05)',
                                  border: '1px solid rgba(34, 197, 94, 0.15)',
                                  borderRadius: 6,
                                  fontSize: 12,
                                  color: colors.muted_foreground,
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 8
                                }}
                              >
                                <span style={{ color: '#22c55e', fontSize: 10, marginTop: 1 }}>●</span>
                                {tip}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'advanced',
                label: '🔧 Advanced',
                children: (
                  <div style={{ marginTop: 16 }}>
                    <Typography.Text style={{ 
                      color: colors.muted_foreground, 
                      fontSize: 13,
                      display: 'block',
                      marginBottom: 16
                    }}>
                      Fine-tune your generation with advanced controls
                    </Typography.Text>
                    {Object.entries(groupedParams).map(([group, params]) => {
                      const requiredParams = params.filter((p) => Number(p.is_required) === 1 || p.is_required === true);
                      const optionalParams = params.filter((p) => !(Number(p.is_required) === 1 || p.is_required === true));
                      
                      // Get group descriptions
                    const getGroupDescription = (groupName) => {
                      const descriptions = {
                        'Sampler': 'Choose generation method (affects style & speed)',
                        'Lora': 'Optional: Apply fine-tuned styles/models',
                        'Dimensions': 'Set image size (default 1024×1024)',
                        'Quality': 'Control output quality and detail level',
                        'Style': 'Artistic style and aesthetic preferences',
                        'Control': 'Advanced generation control parameters'
                      };
                      return descriptions[groupName] || `Configure ${groupName.toLowerCase()} settings`;
                    };
                    
                    return (
                      <React.Fragment key={group}>
                        <div style={{ marginBottom: 16 }}>
                          <Typography.Title level={5} style={{ 
                            color: colors.foreground,
                            marginBottom: 4,
                            fontSize: 14,
                            fontWeight: 600
                          }}>
                            {group}
                          </Typography.Title>
                          <Typography.Text style={{ 
                            color: colors.muted_foreground,
                            fontSize: 12,
                            display: 'block',
                            marginBottom: 12
                          }}>
                            {getGroupDescription(group)}
                          </Typography.Text>
                        </div>
                        
                        {/* Required params always visible */}
                        {requiredParams.map(renderInputParameter)}
                        
                        {/* Optional params in enhanced collapsible */}
                        {optionalParams.length > 0 && (
                          <Collapse
                            ghost
                            style={{ 
                              background: 'rgba(255,255,255,0.02)', 
                              margin: '0 0 16px 0', 
                              padding: 0,
                              borderRadius: 8
                            }}
                          >
                            <Collapse.Panel
                              header={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ color: colors.muted_foreground, fontSize: 13 }}>
                                    Show optional {group.toLowerCase()} settings
                                  </span>
                                  <span style={{ 
                                    background: 'rgba(140, 140, 140, 0.2)',
                                    color: '#8c8c8c',
                                    fontSize: 10,
                                    padding: '2px 6px',
                                    borderRadius: 4,
                                    fontWeight: 500
                                  }}>
                                    {optionalParams.length}
                                  </span>
                                </div>
                              }
                              key='optional'
                              style={{ 
                                background: 'none', 
                                padding: 0,
                                border: 'none'
                              }}
                            >
                              <div style={{ padding: '8px 0' }}>
                                {optionalParams.map(renderInputParameter)}
                              </div>
                            </Collapse.Panel>
                          </Collapse>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              ),
            },
          ]}
          style={{ marginBottom: 16 }}
        />
        )}

        {/* Compact Reset Button */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          marginTop: 16,
          marginBottom: 8
        }}>
          <Button 
            onClick={handleReset} 
            type='text'
            size='small'
            style={{ 
              color: colors.muted_foreground,
              fontSize: '11px',
              padding: '4px 12px',
              height: 'auto',
              border: 'none',
              background: 'transparent'
            }}
          >
            Reset All
          </Button>
        </div>
      </div>
    
      {/* Sticky Footer Generate Button */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: `linear-gradient(to top, ${colors.background} 0%, ${colors.background} 80%, transparent 100%)`,
          // padding: '16px 0 24px 0',
          marginTop: 'auto'
        }}
      >
        <div style={{ position: 'relative' }}>
          {/* Animated gradient border */}
          <div
            style={{
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              borderRadius: 12,
              background: `linear-gradient(
                45deg,
                #1677ff,
                #722ed1,
                #fa8c16,
                #52c41a,
                #1677ff
              )`,
              backgroundSize: '300% 300%',
              animation: 'gradientFlow 3s ease-in-out infinite',
              opacity: submitForm.isLoading ? 0.8 : 1
            }}
          />
          
          <Button
            type='primary'
            size='large'
            loading={submitForm.isLoading}
            onClick={onHandleGenerateImage}
            disabled={isGenerateDisabled || !model?.model_id}
            style={{
              width: '100%',
              // height: 52,
              fontSize: 16,
              fontWeight: 600,
              position: 'relative',
              zIndex: 1,
              background: 'linear-gradient(135deg, #1677ff, #722ed1)',
              border: 'none',
              borderRadius: 10,
              // boxShadow: '0 4px 20px rgba(24, 119, 242, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12
            }}
          >
            {submitForm.isLoading ? (
              <>
                <span>Generating...</span>
              </>
            ) : (
              <>
                <span style={{ fontSize: 18 }}>✨</span>
                <span>{model?.model_id ? 'Generate' : 'Select a model'}</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Full overlay model selector */}
      {isPickerOpen && (
        <div
          className='model-selector-overlay'
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 4000,
            display: 'flex',
            flexDirection: 'column',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)'
          }}
        >
          {/* Overlay header with search and close */}
          <div
            style={{
              padding: 16,
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(0,0,0,0.35)'
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.foreground,
                  whiteSpace: 'nowrap'
                }}
              >
                Select a Model
              </div>
              <Input
                ref={searchInputRef}
                placeholder='🔍 Search models...'
                allowClear
                value={searchValue}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchValue(val);
                  // debounce API to avoid spamming and race conditions
                  debouncedFetchRef.current(
                    val,
                    modelsState?.params,
                    fetchModels
                  );
                }}
                suffix={modelsState?.loading ? <LoadingOutlined spin /> : <SearchOutlined />}
                style={{ flex: 1, height: 40 }}
              />
              <Button onClick={() => setIsPickerOpen(false)} style={{ height: 40 }}>
                Close
              </Button>
            </div>
            {/* Model type filter tags removed */}
          </div>

          {/* Overlay grid */}
          <div className='no-scrollbar' style={{ flex: 1, overflow: 'auto', padding: 16 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 12
              }}
            >
              {(modelsState?.data || []).map((m) => renderModelGridCard(m))}
            </div>
            {!modelsState?.loading && (modelsState?.data || []).length === 0 && (
              <div style={{ color: colors.muted_foreground, fontSize: 12, padding: 8 }}>
                No models found
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default ModelConfigCol;

// Add CSS for animations and hover effects
const style = document.createElement('style');
style.textContent = `
  @keyframes pulse {
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.1); }
  }
  
  @keyframes gradientFlow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  
  @keyframes glow {
    0%, 100% { box-shadow: 0 0 30px rgba(24, 119, 242, 0.15), 0 8px 32px rgba(0,0,0,0.2); }
    50% { box-shadow: 0 0 40px rgba(24, 119, 242, 0.25), 0 8px 32px rgba(0,0,0,0.2); }
  }
  
  .generate-tooltip {
    opacity: 0 !important;
  }
  
  div:hover .generate-tooltip {
    opacity: 1 !important;
  }
`;
document.head.appendChild(style);
